// Kula Revise labs — the ONE content contract (KULA-REVISE-LAB-ENGINE-PLAN.md §3).
//
// A run is one lesson, one knowledge check, one Practice Lab paper or one
// skeleton builder: the thing an author writes, validates and signs off as a
// whole, and the thing a learner plays end to end in one slide. Every run is
// the same shell (cover → screens → results) and differs by `profile`.
//
// This module is deliberately PURE and server-safe: no React, no
// `import.meta.env`, no Supabase. The MCP server imports it directly (see
// mcp/CLAUDE.md, "the golden rule"), and so do the validator, the renderer and
// the migration scripts — one definition, so none of them can drift.
//
// Three rules from the two authors' notes are baked into the shape rather than
// left to discipline, because each was learned the hard way:
//   1. `assessed` is explicit on every question. The old engine scored an item
//      iff it carried a `level`, so a forgotten tag silently dropped a question
//      from the total with nothing on screen.
//   2. `correct` is an authored option index, never a display letter. Options
//      shuffle at render; feedback references options as `{optN}` and the
//      renderer resolves N to the dealt letter.
//   3. One field name for feedback (`feedback`) and one for tags (`tag`) on
//      every screen type, so a validator can enumerate every rendered field.
//      Two engines storing feedback under different names is what made the
//      language gate under-report for an entire build.

export type ReviseBoard = 'nsc' | 'ieb' | 'ib';
export type ReviseProfile = 'lesson' | 'check' | 'paper' | 'skeleton' | 'workshop' | 'source-paper';

export const REVISE_BOARDS: readonly ReviseBoard[] = ['nsc', 'ieb', 'ib'];
export const REVISE_PROFILES: readonly ReviseProfile[] = ['lesson', 'check', 'paper', 'skeleton', 'workshop', 'source-paper'];

/** The seven NSC skills; each selects one skills-guide popup in the paper profile. */
export type NscSkill =
  | 'extraction'
  | 'concept'
  | 'interpretation'
  | 'analysis'
  | 'reliability'
  | 'comparison'
  | 'synthesis';

export const NSC_SKILLS: readonly NscSkill[] = [
  'extraction',
  'concept',
  'interpretation',
  'analysis',
  'reliability',
  'comparison',
  'synthesis',
];

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

export interface ReviseCover {
  /** e.g. "Kula Revise · Grade 12 History · Cold War" — names the strand, not the unit. */
  eyebrow: string;
  title: string;
  /** The hook lines under the title (usually one to three). */
  lines: string[];
  /** Button label, e.g. "Begin Lesson 1". */
  begin: string;
  /** e.g. "About 25 minutes. Lesson 1 of 5." */
  time?: string;
  /**
   * Whether the learner may skip straight to the results/next run. Lesson 1
   * carries true; a capstone carries false so it cannot be skipped past.
   */
  skippable?: boolean;
}

/** One results band. Bands are matched by the highest `min` ≤ the learner's percentage. */
export interface ReviseBand {
  /** Inclusive lower bound, as a percentage 0–100. */
  min: number;
  label: string;
  text: string;
}

export interface ReviseResults {
  eyebrow: string;
  title: string;
  bands: ReviseBand[];
  /** What comes next, shown under the score. */
  done?: { label?: string; title?: string; text?: string };
  footer?: string;
}

// ---------------------------------------------------------------------------
// Assets: sources and images are run-level and referenced by key/id
// ---------------------------------------------------------------------------

export type ReviseLicence = 'clear' | 'flagged' | 'original-svg';

/**
 * A hosted image. Never base64 in the run — the data would carry a copy of
 * every photograph on every read. The licence record is Dylan's
 * COPYRIGHT-FLAGS.md moved into the data where the builder can show it.
 */
export interface ReviseImage {
  id: string;
  /** Public URL (course-assets bucket). */
  url: string;
  /** Factual and non-revealing: what is shown, not what it means. */
  alt: string;
  credit?: string;
  licence: ReviseLicence;
  /** One line: rights holder and why it clears or flags. */
  licenceNote?: string;
  width?: number;
  height?: number;
}

export interface ReviseSource {
  /** Referenced by `source` screens and by paper questions, e.g. "1A". */
  key: string;
  /** Display label, e.g. "Source 1A". */
  label: string;
  /** e.g. "Speech Extract", "Political Cartoon". */
  type?: string;
  /** Light HTML body. Verbatim for public-domain material; a summary otherwise. */
  body?: string;
  imageId?: string;
  attribution: string;
  /** True when the body is Kula's summary of material in copyright; the screen says so. */
  summaryNotice?: boolean;
  /** Optional expander note under the source ("'Liberal' here means…"). */
  toggle?: string;
  /**
   * Paper comparison panel: the keys of the sources it compares, e.g. ["1A", "1B"].
   * The player offers each one in full beside the extracts. Runs written before
   * this field fall back to the keys named in `label`.
   */
  compares?: string[];
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

interface ScreenBase {
  /** Stable within the run. Assessed questions use it as the tracking key. */
  id: string;
  /** Older lesson engine's phase label; optional everywhere. */
  stage?: string;
}

export interface StoryScreen extends ScreenBase {
  type: 'story';
  /** A year or date in the margin, e.g. "1963". */
  marker?: string;
  /** e.g. "Rivonia, Johannesburg". */
  markerLabel?: string;
  lines: string[];
}

export interface ContentScreen extends ScreenBase {
  type: 'content';
  tag?: string;
  html: string;
  /** Drives the key-term modal. */
  terms?: Array<{ term: string; def: string }>;
  /** The boxed takeaway. */
  remember?: string;
}

export interface StatScreen extends ScreenBase {
  type: 'stat';
  tag?: string;
  heading: string;
  lead?: string;
  stats: Array<{ num: string; label: string }>;
}

export interface SourceScreen extends ScreenBase {
  type: 'source';
  tag?: string;
  /** Teaching copy shown above the source: how to read it before the learner does. */
  html?: string;
  sourceKey: string;
}

export interface CarouselScreen extends ScreenBase {
  type: 'carousel';
  tag?: string;
  heading: string;
  cards: Array<{ lead: string; body: string }>;
}

/** The running summary, assembled from `part` strings on build questions. */
export interface RunsumScreen extends ScreenBase {
  type: 'runsum';
  tag?: string;
  heading: string;
  label?: string;
}

/** Numbered points revealed one at a time: a lead line, then the body under it. */
export interface RevealScreen extends ScreenBase {
  type: 'reveal';
  eyebrow?: string;
  heading: string;
  lead?: string;
  items: Array<{ lead: string; body?: string }>;
  close?: string;
}

/**
 * The rubric ladder: the board's dimensions and their bands. Never carries a
 * mark value per dimension — the IEB rubric is holistic and no dimension has
 * its own pot; a line that said otherwise taught something false.
 */
export interface RubricScreen extends ScreenBase {
  type: 'rubric';
  heading?: string;
  intro?: string;
  dimensions: Array<{ name: string; bands: Array<{ label: string; text: string }> }>;
}

export interface PlanScreen extends ScreenBase {
  type: 'plan';
  heading: string;
  intro?: string;
  rows: Array<{ k: string; yes: string; no: string }>;
}

/**
 * The paragraph builder: slots fill in order and the paragraph assembles live.
 * Wrong options lock individually and each carries its own cost line.
 */
export interface BuilderScreen extends ScreenBase {
  type: 'builder';
  eyebrow?: string;
  heading: string;
  lead?: string;
  /** The stand the paragraph is defending, shown throughout. */
  stand?: string;
  slots: Array<{
    label: string;
    options: Array<{ text: string; correct: boolean; cost?: string }>;
  }>;
  close?: string;
}

export interface MtaIntroScreen extends ScreenBase {
  type: 'mta-intro';
  heading: string;
  html: string;
}

/**
 * Mark This Answer: a sample script written badly on purpose; the learner
 * judges each criterion before seeing the examiner's verdict. Nothing about
 * the expected band is visible until they commit.
 */
export interface MtaScreen extends ScreenBase {
  type: 'mta';
  label: string;
  script: string;
  criteria: Array<{
    id: string;
    prompt: string;
    correct: 'yes' | 'partial' | 'no';
    feedback: string;
  }>;
  /** Revealed after all criteria are judged. Mark range only, never a level. */
  band?: string;
}

export type QuestionMode = 'mcq' | 'multi' | 'order' | 'sort' | 'build' | 'sentence-mark';

export interface QuestionScreen extends ScreenBase {
  type: 'question';
  mode: QuestionMode;
  /** EXPLICIT. Unassessed questions (instinct openers, build) never score. */
  assessed: boolean;
  /** Older lesson engine's question kind; `instinct` is always unassessed. */
  kind?: 'instinct' | 'everyday' | 'reveal' | 'decision';
  /** One of the board's tag sets (lib/revise/tags.ts). Required when assessed. */
  tag?: string;
  /** A total, or 'practice'. Required when assessed. */
  marks?: number | 'practice';
  /** Results-recap label, ≤ 30 characters. Required when assessed. */
  short?: string;
  stem: string;
  /**
   * Explains why, in the second person, naming what the error costs.
   * A string is shared feedback with `{optN}` → dealt letter. An array is one
   * line per authored option (the Skills Labs' shape). Same field either way,
   * so a language gate can enumerate it.
   */
  feedback: string | string[];
  /**
   * Mark This Answer as a question: a sample script shown above the stem, and
   * the learner judges what a marker could do with it.
   */
  script?: string;

  // mcq | multi | sentence-mark
  /** Authored order; index 0 is the correct answer by convention. */
  options?: string[];
  /** mcq / sentence-mark: one index. multi: the correct set. acceptAny: any listed index. */
  correct?: number | number[];
  /** Either/or memos: any listed `correct` index earns the mark (single choice). */
  acceptAny?: boolean;
  /** multi: how many the learner must pick (exactly). Defaults to correct.length. */
  maxSelect?: number;

  // order | sort
  items?: string[];
  /** order: the correct sequence of item indices. */
  sequence?: number[];
  /** sort: column labels. */
  cols?: string[];
  /** sort: item index → column index. */
  placement?: Record<string, number>;

  // sentence-mark: `options` are the sentences; `correct` is the weak one.
  rewrite?: string;

  // build: unassessed; `part` feeds the runsum screen
  part?: string;

  // paper profile
  /** Which source panel to show beside the question. */
  sourceKey?: string;
  /** Which skills guide the coach button opens. */
  skill?: NscSkill;
  /** Recommended seconds. Default marks × 45, floor 30. */
  timeSec?: number;
  /** Exam sub-question number as printed, e.g. "1.1.1". */
  number?: string;

  // check profile
  /** Slot number: the same fact in sitting A and B carries the same slot. */
  slot?: number;
  section?: string;
}

// skeleton profile
/** Choose the line (of argument, of a topic sentence) that this part of the plan will carry. */
export interface PickScreen extends ScreenBase {
  type: 'pick';
  tag?: string;
  heading: string;
  intro?: string;
  options: string[];
  /** Authored index of the line that argues. */
  correct: number;
  /** Shown after the choice; explains why the others do not hold. */
  feedback: string;
  /** Label for the line this pick writes into the saved plan. */
  save: string;
  /** Progress label, e.g. "Line of argument". */
  prog?: string;
}

/** Tap the facts that back THIS claim; leave out the real facts that belong elsewhere. */
export interface EvidenceScreen extends ScreenBase {
  type: 'evidence';
  tag?: string;
  heading: string;
  intro?: string;
  /** The topic sentence the evidence must support. */
  claim: string;
  items: Array<{ text: string; use: boolean; off?: string }>;
  feedback: string;
  save: string;
  prog?: string;
}

/** The skeleton's closing frame: the assembled plan, ready to write from. */
export interface FrameScreen extends ScreenBase {
  type: 'frame';
  heading?: string;
  html?: string;
}

export type ReviseScreen =
  | StoryScreen
  | ContentScreen
  | StatScreen
  | SourceScreen
  | CarouselScreen
  | RunsumScreen
  | RevealScreen
  | RubricScreen
  | PlanScreen
  | BuilderScreen
  | MtaIntroScreen
  | MtaScreen
  | QuestionScreen
  | PickScreen
  | EvidenceScreen
  | FrameScreen;

export type ReviseScreenType = ReviseScreen['type'];

export const REVISE_SCREEN_TYPES: readonly ReviseScreenType[] = [
  'story',
  'content',
  'stat',
  'source',
  'carousel',
  'runsum',
  'reveal',
  'rubric',
  'plan',
  'builder',
  'mta-intro',
  'mta',
  'question',
  'pick',
  'evidence',
  'frame',
];

// ---------------------------------------------------------------------------
// Profile blocks
// ---------------------------------------------------------------------------

export interface PaperBlock {
  /** e.g. "DBE May/June 2025". Practice Labs are built from real papers and say so. */
  paperRef: string;
  bigQuestion?: string;
  totalMarks: number;
  recommendedMinutes?: number;
  realExamMinutes?: number;
  /** Per-paper worked example per guide; a missing key falls back to the generic text. */
  guideExamples?: Partial<Record<NscSkill, string>>;
  /** Per-question and global timers. Default on; "a guide, not a penalty". */
  timed?: boolean;
}

export interface CheckBlock {
  sitting: 'before' | 'after';
  /**
   * Shared by sitting A and B of one unit. The after-sitting reads the
   * before-sitting's stored result by this key to show the comparison.
   */
  pairKey: string;
  /** Section names, in order, for the results grouping. */
  sections?: string[];
}

export interface LessonBlock {
  /** Position line source, e.g. 1 of 5. */
  number?: number;
  of?: number;
  /** Which spines this lesson uses; a capstone mixes both. */
  spines?: Array<'source' | 'essay'>;
}

// ---------------------------------------------------------------------------
// The workshop block (profile 'workshop'): one essay question worked from
// first reading to a finished essay. Read from Leah's IB essay workshops
// (causes of war: one war chosen from several; women and war: two wars) as
// re-issued on 14 October 2026, and from her project instructions and the
// women-and-war developer brief (KULA-REVISE-WORKSHOP-PLAN.md §0c).
// Everything a learner reads is a field, so the register rules reach it;
// everything the engine decides with is data, so a fourth question needs no
// fourth engine.
//
// The one structural idea: a workshop is the question plus one EDITION PER
// CASE STUDY. An edition is everything that belongs to one war, state or
// movement: its themes with their evidence, its facts, revision pages,
// historians, perspectives and checks. A one-case question plays one
// edition; a two-case question lays two side by side. The content that
// names BOTH cases at once (topic sentences, positions, the context line,
// the thesis) is generated from each edition's per-theme lines, and a
// hand-written `pairs` entry replaces the generated text for one pair.
// ---------------------------------------------------------------------------

/** The five label colours the question-parts step can paint with. */
export type WorkshopLabelKey = 'command' | 'what' | 'who' | 'time' | 'idea';

/** The IB regions, for the region rule ("each chosen from a different region"). */
export const IB_REGIONS = ['Africa and the Middle East', 'the Americas', 'Asia and Oceania', 'Europe'] as const;
export type IbRegion = (typeof IB_REGIONS)[number];

export interface WorkshopCaseStudy {
  /** Chip label, e.g. "WWI". */
  short: string;
  /** With its article, mid-sentence, e.g. "the First World War". */
  name: string;
  /** Heading, e.g. "First World War, 1914 to 1918". */
  head: string;
  /** Required when the workshop's selection applies the region rule. */
  region?: IbRegion;
  /** Lower-case fragments that count as naming this case study in a learner's writing. */
  match: string[];
  about?: string;
}

/**
 * How the learner opens the workshop, on the start screen. `fixed` names the
 * case study or pair; `pick` lets them choose one or two of `caseStudies`.
 * With `regionRule`, two case studies from one region are refused and the
 * engine says why (copy key `regionRefused`).
 */
export type WorkshopSelection =
  | { fixed: string[] }
  | {
      pick: 1 | 2;
      regionRule?: boolean;
      /** The line above the choice; default from copy key `chooseLede`. */
      lede?: string;
    };

/** The five lines a two-case theme supplies so the pair's sentences can be built (Leah, 14 Oct 2026). */
export interface WorkshopCompareLines {
  /** What changed, as a predicate on this case: "pulled women into men's jobs". */
  changed: string;
  /** What limited or reversed it: "this work did not outlast the fighting". */
  limited: string;
  /** A reason line for a position: "the vote and new legal rights lasted in Britain". */
  reason: string;
  /** A counter line against a position. */
  counter: string;
  /** The "you will need to show" line for a topic sentence on this theme. */
  show: string;
}

export interface WorkshopTheme {
  name: string;
  /** Lower-case form used mid-sentence. */
  low: string;
  /** How it reads in the essay outline, e.g. "women's work". */
  out: string;
  what: string;
  question: string;
  /** Chip above the name in a ranking, e.g. "Long-term". */
  term?: string;
  /** Evidence summary for this case study, for the table. */
  evidence: string;
  /** The one matching sentence for this case study on the match step. */
  matchLine: string;
  /** Lower-case fragments that show a paragraph connects to THIS theme; enables the connection check. */
  words?: string[];
  /** Required on a two-case workshop's themes unless every reachable pair is hand-written in `pairs`. */
  compare?: WorkshopCompareLines;
}

export interface WorkshopFact {
  /** Index into the edition's themes. */
  theme: number;
  text: string;
  /** Lower-case fragments the writing check looks for. */
  keywords: string[];
  /** "shows ..." phrase used when the fact belongs in another paragraph. */
  use: string;
  /** Neutral note behind the "?" on the fact. */
  note?: string;
  /** Flashcard question whose answer is `text`; required when a recall screen names the fact. */
  cue?: string;
}

export interface WorkshopCard {
  tag: string;
  head: string;
  points: string[];
}

/** A key fact a recall screen checks against, with its flashcard question. */
export interface WorkshopKeyFact {
  text: string;
  cue: string;
}

export interface WorkshopRevisionPage {
  /** Small label above the lede, e.g. "Long-term cause 1 of 5" or "Perspectives". */
  label?: string;
  lede: string;
  cards: WorkshopCard[];
  /**
   * When present, a recall-from-memory screen follows the page, and its key
   * facts go into the flashcard deck when missed. `facts` names fact ids
   * (each needs a `cue`); `keys` are key facts of their own. One or the other.
   */
  recall?: { name: string; facts?: string[]; keys?: WorkshopKeyFact[] };
}

export interface WorkshopHistorian {
  name: string;
  /** Sentence opener, e.g. "Arthur Marwick argues that ". */
  opener: string;
  /** Lower-case fragments that count as citing this historian. */
  match: string[];
}

export interface WorkshopTopicSentence {
  text: string;
  /** The "you will need to show" note under it. */
  need: string;
}

export interface WorkshopPosition {
  title: string;
  detail: string;
  /** The thesis stem the engine builds "..., because ..." onto. */
  build: string;
  reasons: Array<{ theme: number; text: string }>;
  counters: Array<{ theme: number; text: string }>;
}

/**
 * One clause of a position hint. Either count the (theme, case study) cells
 * the learner judged with `judgement` on the compare step, or read where a
 * theme sits in the learner's ranking (0 = top). The clause holds when the
 * value is within [min, max].
 */
export type WorkshopHintClause =
  | { judgement: number; themes?: 'all' | 'main' | 'counter'; caseStudy?: 'both' | 'first' | 'second'; min?: number; max?: number }
  | { rankOf: 'anchor' | number; min?: number; max?: number };

export interface WorkshopHint {
  position: number;
  when: WorkshopHintClause[];
}

export interface WorkshopHints {
  /** First hint whose clauses all hold names the suggested position; else `fallback`. */
  order: WorkshopHint[];
  fallback: number;
}

export interface WorkshopChecks {
  /** Lower-case fragments that show the writing engages the claim (intro position, conclusion answer). */
  claim: string[];
  /** Lower-case fragments that show a paragraph's last sentence links back. */
  link: string[];
  /** Theme vocabulary the intro's outline check counts; the outline passes at `outlineHits`. */
  themeWords: string[];
  outlineHits?: number;
  /** Years that count as an exact date, inclusive. Default 1800 to 1999. */
  yearRange?: [number, number];
  /** What to say when the writing does not name the case study. */
  caseMessage?: string;
  /**
   * Extra checks on the introduction and conclusion: every list in `all` must
   * have at least one fragment present. "Long and short term" is one of these.
   */
  extra?: Array<{ label: string; all: string[][]; yes: string; no: string }>;
}

/** The prose that differs from one workshop to the next; the engine holds a default for each key. */
export type WorkshopCopyKey =
  | 'chooseLede'
  | 'regionRefused'
  | 'matchLede'
  | 'matchLede1'
  | 'matchLede2'
  | 'matchHowto'
  | 'matchTableHowto'
  | 'argueLede'
  | 'balanceHead'
  | 'balance'
  | 'compareHowto'
  | 'compareSummary'
  | 'comparePositionNote'
  | 'bothSame'
  | 'bothDifferent'
  | 'rankHowto'
  | 'rankTally'
  | 'rankTest'
  | 'positionLabel'
  | 'positionNote'
  | 'positionYes'
  | 'positionNo'
  | 'answerNo'
  | 'linkNo'
  | 'analysisNo'
  | 'outline'
  | 'paragraphsLede'
  | 'leftOut'
  | 'conclusionAnswerStarter'
  | 'connectionStarterAnchor'
  | 'connectionStarterOther'
  | 'recallExample'
  | 'recallExampleAnchor'
  | 'coachExample'
  // The pair templates (lib/revise/workshop/pair.ts): how two case studies'
  // compare lines become one sentence. {A} / {B} the case studies, {changedA}
  // and so on their lines, {title} a reading's title.
  | 'pairContext'
  | 'tsSimilar'
  | 'tsLimited'
  | 'tsContrast'
  | 'tsNeed'
  | 'posBuild'
  | 'pairReason'
  | 'pairCounter'
  | 'pairMixedReason'
  | 'pairMixedCounter';

/** One case study's content. Keyed in `editions` by the case-study id. */
export interface WorkshopEdition {
  /** The one-sentence context line for the introduction. */
  context: string;
  /** Themes in order. On a two-case workshop every edition carries the same names in the same order. */
  themes: WorkshopTheme[];
  /** Index of the "factors beyond ..." theme, when the question has one. */
  counterTheme?: number;
  /** Index of a theme that always gets a paragraph (the short-term cause). */
  anchorTheme?: number;
  /** The fact bank for this case study. */
  facts: Record<string, WorkshopFact>;
  /** Revision pages in order; a page with `recall` is followed by a recall screen. */
  revision: WorkshopRevisionPage[];
  historians: Record<string, WorkshopHistorian>;
  /** Per theme: fact ids from OTHER themes offered as distractors when selecting facts. */
  elsewhere: string[][];
  /** Per theme: the historians on offer and how each reads the theme. A historian may appear twice. */
  perspectives: Array<Array<{ historian: string; line: string }>>;
  /** Per theme: three topic sentences arguing different things. Required for a one-case workshop; a two-case one generates them. */
  topicSentences?: WorkshopTopicSentence[][];
  /** The three readings as positions. Required for a one-case workshop; a two-case one generates them. */
  positions?: WorkshopPosition[];
  /** Which position the learner's judgements point at. Required with `positions`. */
  hints?: WorkshopHints;
  checks: WorkshopChecks;
  /** Coach quick answers per step id (and `write` for every written part). */
  quick: Record<string, Array<[string, string]>>;
  copy: Partial<Record<WorkshopCopyKey, string>>;
}

/** Hand-written content for one pair, replacing the generated text for that pair wholesale. */
export interface WorkshopPairOverride {
  context?: string;
  topicSentences?: WorkshopTopicSentence[][];
  positions?: WorkshopPosition[];
  hints?: WorkshopHints;
  checks?: Partial<WorkshopChecks>;
}

/** The key of a pair in `pairs`: the two case-study ids sorted and joined with "+". */
export const workshopPairKey = (a: string, b: string): string => [a, b].sort().join('+');

/** Which way a reading of the claim leans. */
export type WorkshopStance = 'for' | 'against' | 'mixed';

export type WorkshopCommandTerm = 'discuss' | 'evaluate' | 'to-what-extent' | 'examine' | 'compare-contrast';

export interface WorkshopBlock {
  question: string;
  /** Shapes the argue and plan steps and the paragraph check (§0c). */
  commandTerm: WorkshopCommandTerm;
  /** With the AI coach (panel, checks, reads) or without it. Off keeps the quick check and the fact notes. */
  coach: boolean;
  /** Exam line on the start screen, e.g. "Paper 2, Topic 11. An essay marked out of 15. About 45 minutes." */
  exam: string;
  brand: { line: string; title: string; titleAccent: string };
  /** The start screen's hook paragraph. */
  intro: string;
  /** The "How it works" cards. */
  how: Array<{ head: string; text: string }>;
  coachStrip: string;
  /** The noun for a case study, e.g. war / wars, state / states. */
  unit: { singular: string; plural: string };
  /** The noun for a theme, e.g. theme / themes, cause / causes. */
  themeWord: { singular: string; plural: string };
  /** The question split into parts, each with the label a learner must give it. */
  parts: Array<{ text: string; label: WorkshopLabelKey }>;
  labels: Array<{ key: WorkshopLabelKey; name: string }>;
  /** What each part asks of you. */
  asks: Array<{ key: WorkshopLabelKey; name: string; head: string; text: string }>;
  watch: { head: string; text: string };
  caseStudies: Record<string, WorkshopCaseStudy>;
  selection: WorkshopSelection;
  /** One edition per case study, keyed by its id. */
  editions: Record<string, WorkshopEdition>;
  /** Hand-written pair content, keyed by `workshopPairKey`. */
  pairs?: Record<string, WorkshopPairOverride>;
  /** The three readings of the claim. `stance` says which way each leans, for the generated positions; default by index: for, against, mixed. */
  readings: Array<{ label: string; title: string; text: string; stance?: WorkshopStance }>;
  jobs: Array<{ name: string; text: string }>;
  /** The three judgements per theme per case study on the compare step; required when the learner has two case studies. */
  scale?: { labels: string[]; text: string[] };
  /** Relevant facts a paragraph plan must hold, with at least one per case study. */
  minFacts: number;
  writing: {
    introMust: string[];
    conclusionMust: string[];
    /** Notes beside the box (never inserted into the text); default Analysis / Judging the view / Link. */
    paragraphStarters?: Array<{ label: string; text: string }>;
    conclusionStarters?: Array<{ label: string; text: string }>;
  };
}

/** Body paragraphs the learner chooses between on the ranking screen (Leah, rule 17). */
export const WORKSHOP_PARAGRAPH_CHOICES = [3, 4] as const;
export type WorkshopParagraphCount = (typeof WORKSHOP_PARAGRAPH_CHOICES)[number];

/** Word targets by part, [low, high]; the check window is 20 under and 30 over (the brief, §2). */
export const WORKSHOP_WORD_TARGETS = {
  intro: [80, 120],
  conclusion: [80, 120],
  paragraph: { 3: [200, 250], 4: [150, 200] },
} as const;

// ---------------------------------------------------------------------------
// The source paper block (profile 'source-paper'): one complete IB History
// Paper 1, four sources and five questions, practised question by question
// or sat against the clock. Read from Leah's engine specification (15 October
// 2026) and her reference build, Italy and Abyssinia
// (KULA-REVISE-IB-PAPER1-PAPER3-PLAN.md §3).
//
// What is fixed by the format (the five roles, their marks and times, the
// markbands, the shared skills pack) lives in the engine, not here. Everything
// written for one practice lives in this block, including the check patterns
// the reference build wrote inline (spec §11).
// ---------------------------------------------------------------------------

/** The five question roles of Paper 1, in the order they are asked. */
export type SourcePaperRole = 'extract' | 'visual' | 'opvl' | 'compare' | 'essay';
export const SOURCE_PAPER_ROLES: readonly SourcePaperRole[] = ['extract', 'visual', 'opvl', 'compare', 'essay'];

/** The fixed format (spec §3). A practice that disagrees is refused, never adjusted. */
export const SOURCE_PAPER_FORMAT = {
  sources: 4,
  totalMarks: 24,
  readingMinutes: 5,
  writingMinutes: 60,
  roles: {
    extract: { marks: 3, minutes: 5, sources: 1 },
    visual: { marks: 2, minutes: 5, sources: 1 },
    opvl: { marks: 4, minutes: 10, sources: 1 },
    compare: { marks: 6, minutes: 15, sources: 2 },
    essay: { marks: 9, minutes: 25, sources: 4 },
  },
} as const;

/**
 * A value that may differ by the chosen version of the variant source: one
 * value for every version, or one per version keyed by its id. The explicit
 * wrapper keeps a per-version object from being mistaken for a single value.
 */
export type SourcePaperByVariant<T> = T | { byVariant: Record<string, T> };

/** Copyright record for one source (spec §12). Leah records it; the validator warns while it is missing. */
export interface SourcePaperCopyright {
  status: 'public-domain' | 'cleared' | 'flagged';
  /** Author's date of death, rights holder or why it clears. */
  note?: string;
}

/** One version of a source as it appears in the booklet. */
export interface SourcePaperSourceBody {
  type: 'text' | 'image';
  /** The attribution in IB style: who, their role, the date, the type of source, where it was published. */
  who: string;
  /** Text sources: the source text, one string per paragraph. Ellipses in square brackets mark cuts. */
  paragraphs?: string[];
  /** A short glossary line under the source. */
  gloss?: string;
  /** Image sources: a `run.images` id. The alt text lives on the image. */
  imageId?: string;
  /** Image sources: the caption, shown in italics under the image. */
  caption?: string;
  copyright?: SourcePaperCopyright;
}

/** One version of a variant source, with the label the start screen offers it under. */
export interface SourcePaperVariant extends SourcePaperSourceBody {
  /** e.g. "Samuel Hoare's speech to the House of Commons, December 1935". */
  label: string;
  /** Short line for the download heading, e.g. "Samuel Hoare, December 1935". */
  short: string;
}

/** A source in the booklet: one body, or two or more versions the learner chooses between. */
export type SourcePaperSource = SourcePaperSourceBody | { variants: Record<string, SourcePaperVariant> };

export interface SourcePaperQuestion {
  /** Stable id, also the saved-answer key, e.g. "q9a". */
  id: string;
  role: SourcePaperRole;
  /** The number as printed, e.g. "9 (a)". It depends on the prescribed subject. */
  n: string;
  marks: number;
  /** Source letters the question uses, in booklet order. */
  sources: string[];
  text: SourcePaperByVariant<string>;
}

/**
 * Check patterns are strings, compiled case-insensitively by the engine. The
 * validator compiles every one and refuses a pattern that matches an empty
 * answer, since that would mark every learner's answer as "in place".
 */
export type SourcePaperPattern = string;

/** A statement to pick (extract and visual): a point the source makes, or a plausible one it does not. */
export interface SourcePaperPickItem {
  id: string;
  /** True when the source makes or suggests this point. */
  ok: boolean;
  t: string;
  /** Feedback. For a wrong item, says why it is wrong: own knowledge, another source, out of the date range, or in no source. */
  why: string;
  /** Required on ok items: finds the point in the learner's answer. */
  kw?: SourcePaperPattern;
}

export interface SourcePaperPickBank {
  items: SourcePaperPickItem[];
  /** Matches content that is not in the source. */
  outsideKw: SourcePaperPattern;
  /** Extract: matches a brief quotation. Visual: matches a visual detail. */
  evidenceKw: SourcePaperPattern;
  /** Above this the answer is long for its marks. */
  maxWords: number;
  /** The instruction above the statements; the engine supplies one per role when absent. */
  howto?: string;
  /** What to point to when the evidence check fails, e.g. "the old man in nightclothes, the giant soldier or the caption". */
  evidenceHint?: string;
}

export interface SourcePaperOpvlItem {
  id: string;
  c: 'Origin' | 'Purpose' | 'Content';
  ans: 'V' | 'L';
  t: string;
  why: string;
}

export interface SourcePaperOpvlBank {
  items: SourcePaperOpvlItem[];
  /**
   * Origin and content must be written for the source. Purpose falls back to
   * the engine's default; focus (the historian's focus in the question, spec
   * §7.5) falls back to a generic "historian|response".
   */
  kw: { origin: SourcePaperPattern; purpose?: SourcePaperPattern; content: SourcePaperPattern; focus?: SourcePaperPattern };
}

export interface SourcePaperCompareItem {
  id: string;
  /** Similarity, difference, or not a comparison (about one source only). */
  ans: 'S' | 'D' | 'N';
  t: string;
  why: string;
  /** Required on S and D items: finds the point in the learner's answer. */
  kw?: SourcePaperPattern;
}

/** One own-knowledge fact for the final question. Every one is relevant; there are no wrong answers. */
export interface SourcePaperOwnKnowledge {
  id: string;
  t: string;
  /** What it is and how to use it. */
  note: string;
  kw: SourcePaperPattern;
  /** Which way it usually cuts: supports, counters, or both. Guidance only; the learner places it. */
  side?: 's' | 'c' | 'b';
}

export interface SourcePaperPerspective {
  id: string;
  /** Short name, e.g. "The great powers killed the League". */
  t: string;
  /** The historian and their view, in your own words. Never an invented quotation. */
  d: string;
  /** e.g. "This view supports the claim." */
  use: string;
  /** Finds this view in an answer, usually the historian's surname. Absent for a school of thought. */
  kw?: SourcePaperPattern;
}

export interface SourcePaperPosition {
  /** e.g. "large", "some", "limited". */
  id: string;
  t: string;
  d: string;
  /** The thesis sentence this position writes into the editable thesis. */
  line: string;
}

export interface SourcePaperEssayBank {
  /** Per source letter: how the source can be used for the final question. */
  stance: Record<string, SourcePaperByVariant<string>>;
  ownKnowledge: SourcePaperOwnKnowledge[];
  perspectives: SourcePaperPerspective[];
  positions: SourcePaperPosition[];
  /** "A shape you can use": the interpretations this question invites, then the conclusion line. */
  shape: string[];
  /** The loaded word in the question, e.g. "responsible". Joins the judgement check. */
  loadedWord: string;
}

/** One revision topic: a screen of reveal tiles, each a cue on the front and a point on the back. */
export interface SourcePaperRevisionCard {
  title: string;
  /** Prompts recall without giving the point away. Same length as `points`. */
  cues: string[];
  points: string[];
  /** Skills pack only: a type table, one tile per source type. */
  types?: Array<{ type: string; values: string; limitations: string }>;
}

export interface SourcePaperBlock {
  /** Stable practice id: the saved-answers key and the download file name, e.g. "italy_abyssinia". */
  id: string;
  /** Hero, line 1, e.g. "Italy and Abyssinia, 1935–1936." */
  title: string;
  /** Hero, line 2, in pink, e.g. "Practise Paper 1." */
  titleAccent: string;
  /** The band reads "IB History · Paper 1 · {brandline}". */
  brandline: string;
  /** The hero paragraph. */
  intro: string;
  /** "The sources and questions relate to Case study 2: …" */
  caseStudy: string;
  /** The "How it works" cards on the start screen. */
  how: Array<{ head: string; text: string }>;
  /** Keyed by letter, in booklet order. Exactly four; at most one has variants. */
  sources: Record<string, SourcePaperSource>;
  /** Required when a source has variants: the version the start screen opens on. */
  defaultVariant?: string;
  /** Five questions, one per role, in role order. */
  questions: SourcePaperQuestion[];
  extract: SourcePaperPickBank;
  visual: SourcePaperPickBank;
  /** Per version when the opvl source has variants. */
  opvl: SourcePaperByVariant<SourcePaperOpvlBank>;
  compare: { items: SourcePaperCompareItem[] };
  essay: SourcePaperEssayBank;
  /**
   * Points examiners might credit, per question id, for the timed self-check.
   * On the essay question the engine replaces the point that begins
   * "Source {letter}:" for the variant source with that version's stance note
   * (spec §6.11), so the list can carry a placeholder there.
   */
  indicative: Record<string, SourcePaperByVariant<string[]>>;
  /** The content revision pack for this practice. The skills pack is engine-level. */
  revise: { title: string; blurb: string; cards: SourcePaperRevisionCard[] };
}

/** The one source with variants and its versions, or null when every source has one body. */
export function sourcePaperVariantSource(p: SourcePaperBlock): { letter: string; variants: Record<string, SourcePaperVariant> } | null {
  for (const [letter, s] of Object.entries(p.sources ?? {})) {
    if (s && typeof s === 'object' && 'variants' in s) return { letter, variants: s.variants };
  }
  return null;
}

/** Resolve a by-variant value for the chosen version. */
export function forVariant<T>(v: SourcePaperByVariant<T>, variant: string | null): T | undefined {
  if (v && typeof v === 'object' && 'byVariant' in (v as object)) {
    const map = (v as { byVariant: Record<string, T> }).byVariant;
    return variant == null ? undefined : map[variant];
  }
  return v as T;
}

/** The body of a source for the chosen version. */
export function sourcePaperBody(s: SourcePaperSource, variant: string | null): SourcePaperSourceBody | undefined {
  if ('variants' in s) return variant == null ? undefined : s.variants[variant];
  return s;
}

// ---------------------------------------------------------------------------
// The run
// ---------------------------------------------------------------------------

export interface ReviseRun {
  /** Contract version. Bump on any breaking change to this file. */
  version: 1;
  profile: ReviseProfile;
  board: ReviseBoard;
  /** Stable identity, e.g. "p1-2024-nov-q1-cold-war" or "ieb-angola-l1". */
  slug?: string;
  /** Unit identity colour (hex). Never carries scoring meaning. */
  accent?: string;
  cover: ReviseCover;
  screens: ReviseScreen[];
  results: ReviseResults;
  sources?: ReviseSource[];
  images?: ReviseImage[];
  paper?: PaperBlock;
  check?: CheckBlock;
  lesson?: LessonBlock;
  /** Required for profile workshop; `screens` is empty there, the steps come from this block. */
  workshop?: WorkshopBlock;
  /** Required for profile source-paper; `screens` is empty there, the steps come from this block. */
  sourcePaper?: SourcePaperBlock;
}

/** Type guard for the discriminator only; the validator does the real work. */
export function isQuestionScreen(s: ReviseScreen): s is QuestionScreen {
  return s.type === 'question';
}

export function assessedQuestions(run: ReviseRun): QuestionScreen[] {
  return run.screens.filter((s): s is QuestionScreen => s.type === 'question' && s.assessed);
}

export function mtaScreens(run: ReviseRun): MtaScreen[] {
  return run.screens.filter((s): s is MtaScreen => s.type === 'mta');
}

/** Recommended seconds for a paper question: marks × 45 with a 30-second floor. */
export function questionTimeSec(q: QuestionScreen): number {
  if (q.timeSec && q.timeSec > 0) return q.timeSec;
  const m = typeof q.marks === 'number' ? q.marks : 1;
  return Math.max(30, m * 45);
}

/** Tracking key used for the stored run summary row. */
export const REVISE_RESULTS_KEY = '__results';
